import { Injectable } from '@angular/core';

import { activitiesList } from '@api';
import type { ActivitiesListResponse } from '@api';

import { emptyOnUnsafeInput } from '@shared/input-safety/empty-on-unsafe-input';

const DEFAULT_PAGE_LIMIT = 100;

export type GetActivitiesOptions = {
  tagPath?: string[];
  searchTerm?: string;
  cursor?: string;
  limit?: number;
  activityType?: string;
  createdByMe?: boolean;
  includeTenantShared?: boolean;
  favoritesOnly?: boolean;
  favoritesFirst?: boolean;
  sortBy?: string;
  sortDir?: string;
  durationValue?: number;
  durationUnit?: string;
  createdFrom?: string;
  createdTo?: string;
};

/**
 * The page a list screen gets for a query the input-safety rule refuses, as for a search with no matches.
 *
 * @returns A fresh empty page with a total of 0 and no next page.
 */
export function emptyActivitiesPage(): ActivitiesListResponse {
  return { items: [], hasMore: false, totalCount: 0 };
}

/** API adapter for the unified `GET /v1/activities` list endpoint. */
@Injectable({ providedIn: 'root' })
export class ActivitiesListApiService {
  /**
   * @returns The page, or an empty page with a total of 0 when the client or Kt.Api rejects the query as unsafe input.
   *   Every other failure rejects.
   */
  list(options: GetActivitiesOptions = {}): Promise<ActivitiesListResponse> {
    const searchTerm = options.searchTerm?.trim();

    return emptyOnUnsafeInput(
      () =>
        activitiesList({
          query: {
            limit: options.limit ?? DEFAULT_PAGE_LIMIT,
            cursor: options.cursor,
            tagPath: options.tagPath,
            searchTerm: searchTerm && searchTerm.length > 0 ? searchTerm : undefined,
            activityType: options.activityType,
            ...(options.createdByMe ? { createdByMe: true } : {}),
            ...(options.includeTenantShared ? { includeTenantShared: true } : {}),
            ...(options.favoritesOnly ? { favoritesOnly: true } : {}),
            ...(options.favoritesFirst ? { favoritesFirst: true } : {}),
            sortBy: options.sortBy,
            sortDir: options.sortDir,
            durationValue: options.durationValue,
            durationUnit: options.durationUnit,
            createdFrom: options.createdFrom,
            createdTo: options.createdTo,
          },
          throwOnError: true,
          responseStyle: 'data' as const,
        }) as unknown as Promise<ActivitiesListResponse>,
      emptyActivitiesPage,
    );
  }
}
